import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import SaakhSetuLoader from '@/components/SaakhSetuLoader';

import SelectFarmer from '@/components/Assessment/SelectFarmer';
import ReviewProfile from '@/components/Assessment/ReviewProfile';
import RepaymentRefresh from '@/components/Assessment/RepaymentRefresh';
import ExternalRefresh from '@/components/Assessment/ExternalRefresh';
import DocumentsReview from '@/components/Assessment/DocumentsReview';
import ReviewSubmit from '@/components/Assessment/ReviewSubmit';
import EnhancementDetails from '@/components/Assessment/EnhancementDetails';
import CultivationChanges from '@/components/Assessment/CultivationChanges';
import FinancialUpdate from '@/components/Assessment/FinancialUpdate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEP_COMPONENTS = {
  select_farmer: SelectFarmer,
  review_profile: ReviewProfile,
  repayment_refresh: RepaymentRefresh,
  external_refresh: ExternalRefresh,
  external_data: ExternalRefresh,
  documents: DocumentsReview,
  review_submit: ReviewSubmit,
  enhancement_details: EnhancementDetails,
  cultivation_changes: CultivationChanges,
  financial_update: FinancialUpdate,
};

const AssessmentWorkflow = ({ user, onLogout }) => {
  const { journeyType } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedFarmerId = searchParams.get('farmerId');

  const [journeyConfig, setJourneyConfig] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [assessment, setAssessment] = useState(null);
  const [farmer, setFarmer] = useState(null);
  const [previousAssessment, setPreviousAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, [journeyType]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/assessments/journey-config/${journeyType}`);
      setJourneyConfig(res.data);
    } catch {
      toast.error('Failed to load journey config');
      navigate('/assessment/new');
    } finally {
      setLoading(false);
    }
  };

  const onFarmerSelected = useCallback(async (selectedFarmer, prevAssessment) => {
    setFarmer(selectedFarmer);
    setPreviousAssessment(prevAssessment);

    // Create assessment in backend
    try {
      const payload = {
        farmer_id: selectedFarmer.id,
        journey_type: journeyType,
        previous_assessment_id: prevAssessment?.id || null,
        sanctioned_limit_previous: prevAssessment?.sanctioned_limit_approved || prevAssessment?.sanctioned_limit_requested || null,
      };
      const res = await axios.post(`${API}/assessments/create`, payload);
      setAssessment(res.data);
      toast.success('Assessment created');
      setCurrentStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create assessment');
    }
  }, [journeyType]);

  const refreshAssessment = useCallback(async () => {
    if (!assessment?.id) return;
    try {
      const res = await axios.get(`${API}/assessments/${assessment.id}`);
      setAssessment(res.data);
    } catch {
      // silent
    }
  }, [assessment?.id]);

  const onUpdateAssessment = useCallback(async (updateData) => {
    if (!assessment?.id) return;
    try {
      const res = await axios.put(`${API}/assessments/${assessment.id}`, updateData);
      setAssessment(res.data);
    } catch (err) {
      toast.error('Failed to update assessment');
    }
  }, [assessment?.id]);

  const handleSubmit = async () => {
    if (!assessment?.id) return;
    setSubmitting(true);
    try {
      // Refresh readiness first
      await axios.post(`${API}/assessments/${assessment.id}/refresh-readiness`);
      // Submit triggers: readiness gate -> scoring pipeline -> persist
      const res = await axios.post(`${API}/assessments/${assessment.id}/submit`);
      setAssessment(res.data);
      if (res.data.assessment_status === 'SCORED') {
        toast.success(`Yogyank: ${res.data.yogyank_score_base ?? res.data.yogyank_score_final} (${res.data.yogyank_score_band})`);
      } else {
        toast.success('Assessment submitted');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.reasons) {
        toast.error(`Cannot submit: ${detail.reasons.slice(0, 3).join(', ')}`);
      } else if (typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Submission failed');
      }
      // Refresh assessment to show latest diagnostics
      await refreshAssessment();
    } finally {
      setSubmitting(false);
    }
  };

  const steps = journeyConfig?.steps || [];
  const currentStepConfig = steps[currentStep];
  const progressPct = steps.length > 0 ? ((currentStep + 1) / steps.length) * 100 : 0;
  const isLastStep = currentStep === steps.length - 1;
  const StepComponent = currentStepConfig ? STEP_COMPONENTS[currentStepConfig.id] : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <SaakhSetuLoader message="Loading workflow..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      {/* Navbar */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/saakhsetu-logo.png" alt="SaakhSetu" style={{ height: '36px' }} />
              <Badge variant="outline" className="text-xs" data-testid="journey-type-badge">
                {journeyConfig?.label || journeyType}
              </Badge>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/assessment/new')} data-testid="back-to-selector-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Change Journey
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Bar */}
        <Card className="mb-6" data-testid="workflow-progress">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Step {currentStep + 1} of {steps.length}: {currentStepConfig?.title}</span>
                <span>{Math.round(progressPct)}% Complete</span>
              </div>
              <Progress value={progressPct} className="h-2" />
              <div className="flex justify-between">
                {steps.map((step, idx) => (
                  <div key={idx} className="flex flex-col items-center flex-1">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs transition-all ${
                        idx < currentStep
                          ? 'bg-green-500 text-white'
                          : idx === currentStep
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {idx < currentStep ? <Check className="h-4 w-4" /> : idx + 1}
                    </div>
                    <div className="mt-1.5 text-[10px] text-center font-medium text-gray-500 hidden sm:block leading-tight">
                      {step.title}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step Content */}
        <Card data-testid="workflow-step-content">
          <CardContent className="pt-6">
            {StepComponent ? (
              <StepComponent
                farmer={farmer}
                assessment={assessment}
                previousAssessment={previousAssessment}
                journeyType={journeyType}
                journeyConfig={journeyConfig}
                user={user}
                onFarmerSelected={onFarmerSelected}
                onUpdateAssessment={onUpdateAssessment}
                refreshAssessment={refreshAssessment}
                onSubmit={handleSubmit}
                submitting={submitting}
                preselectedFarmerId={preselectedFarmerId}
              />
            ) : (
              <div className="text-center py-12 text-gray-500">
                Step component not available for: {currentStepConfig?.id}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            data-testid="workflow-prev-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>

          {!isLastStep && (
            <Button
              onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
              disabled={currentStep === 0 && !assessment}
              data-testid="workflow-next-btn"
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default AssessmentWorkflow;
